import React from 'react';
import { Link } from 'react-router-dom';
import { StarIcon } from '@heroicons/react/24/outline';
import { GamePlayMilestonePayload } from '../../../generated-api';

interface GamePlayMilestoneCardProps {
  payload: GamePlayMilestonePayload;
  createdAt: string;
}

export const GamePlayMilestoneCard: React.FC<GamePlayMilestoneCardProps> = ({ payload, createdAt }) => {
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getMilestoneMessage = (milestone: number) => {
    switch (milestone) {
      case 5: return 'is becoming a favorite!';
      case 10: return 'is a group staple!';
      case 20: return 'is a beloved classic!';
      case 50: return 'has legendary status!';
      case 100: return 'is an all-time favorite!';
      default: return 'reached a milestone!';
    }
  };

  const hasGameImage = payload.gameImage && payload.gameImage.trim() !== '';

  return (
    <Link
      data-track="feed_game_milestone_card"
      to={`/games/${payload.gameId}`}
      className="block bg-surface rounded-lg shadow-sm border border-edge hover:shadow-md transition-shadow duration-200"
    >
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 bg-indigo-100 rounded-full p-2">
              <StarIcon className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-heading">Game Milestone!</p>
              <p className="text-xs text-muted">{formatRelativeTime(createdAt)}</p>
            </div>
          </div>
          {hasGameImage && (
            <img
              src={payload.gameImage!}
              alt={payload.gameName}
              className="h-24 max-w-32 rounded object-contain flex-shrink-0"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
        </div>
        <div className="mt-3">
          <p className="text-sm text-body">
            <span className="font-medium text-indigo-600">{payload.gameName}</span>
            {' has been played '}
            <span className="font-bold text-indigo-600">{payload.milestone} times</span>
            {' and '}
            {getMilestoneMessage(payload.milestone)}
          </p>
        </div>
      </div>
    </Link>
  );
};
